import React, { useState } from 'react';
import { useTheme } from 'emotion-theming';
import styled from 'App/styled';
import { FormattedMessage, useStringFromId } from '@nykaa/intl-shared';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { CartData } from 'store/addToCart/types';
import { pushData } from 'helpers/utils/dataLayer';
import AddToCartSnackbar from '../ItemAddedSnackbar';
import { Variant } from 'shared/store/product/types';
import { ADDING, ADDED_TO_CART, ADD_TO_BAG } from 'constants/strings';
import { AddToCartParams } from 'store/addToCart/action';
import ShoppingBagIcon from 'components/Icons/ShoppingBag';
import { PDP_COMBO_WIDGET } from 'shared/constants/dataLayer';
import { CAB_SECTION_ID } from 'shared/constants/productPage';
import { isDeviceNotWeb } from 'helpers/utils/device';
import {
  PRODUCT_ADDED_TO_CART,
  standardErrorMessages,
  ADD_TO_CART,
  NO_OFFER_MESSAGE,
  NO_OFFER_ID,
} from 'shared/constants';
import { productCardAction } from 'helpers/utils/vernacular/tracking';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { pages } from 'constants/urls';
import { pushEvent, getNavigationTrackingParams } from '@nykaa/data-layer';
import { VariantOptionTypes } from '@nykaa/wishlist-shared/types';
import { MOVE_TO_BAG } from '@nykaa/wishlist-shared/constants';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { PRIVE_CTA_DISABLE } from 'constants/experiments';
import { isValidFunc } from '@nykaa/utils/validations';
import { PageJourney } from 'store/dataLayer/action';
import PageTypes from 'constants/pageTypes';
import ShoppingBagIconAlt from '@nykaa/product-cta/assets/ShoppingBagAlt';
import { getViewBagPageSource } from 'components/Header/RightNav/Notification/helpers';
import { getUpdatedTrackingKeys, isActionDisabled } from '../helper';
import Toast, { VARIANT } from '@nykaa/ui-components/Toast';
import { useIsConvenienceFeeEnabled } from 'shared/hooks';
import { useDealDeltaToast } from 'shared/hooks/useDealDeltaToast';

const SNACKBAR_ID = 'add-to-cart-prompt';
const SCROLL_BEHAVIOR = 'smooth';
const COMBO_QUERY_PARAM = 'combo';
const REDIRECT_FROM = 'rf';
interface ButtonProps {
  background: string;
  isLoading: boolean;
  hideBag?: boolean;
  isOnlyAddToCart?: boolean;
  isDisabled?: boolean;
}

const Button = styled.button`
  height: 36px;
  width: ${({ isOnlyAddToCart }) => (isOnlyAddToCart ? '100%' : '75%')};
  background: ${({ background, isDisabled, theme }: ButtonProps) =>
    isDisabled ? theme.colors.surface40 : background};
  color: #fff;
  border: none;
  ${({ theme }) => theme.typography.buttonLarge};
  display: flex;
  align-items: center;
  justify-content: ${({ hideBag }) => (hideBag ? 'end' : 'center')};
  &:focus {
    outline: none;
  }

  svg {
    vertical-align: middle;
    path {
      fill: ${({ isDisabled, theme: { colors } }) =>
        isDisabled && colors.textDisabled};
    }
  }

  .btn-text {
    margin-left: ${({ theme }) => theme.spacing.spacing40};
    color: ${({ isDisabled, theme: { colors } }) =>
      isDisabled && colors.textDisabled};
  }

  path:nth-of-type(2) {
    fill: ${({ isDisabled, theme: { colors } }) =>
      isDisabled ? colors.textDisabled : colors.white};
  }
`;

const GO_TO_BAG = 'Go To Bag';
interface AddToCartProps {
  isPro: boolean;
  product: Variant | VariantOptionTypes;
  doAddToCart: (params: AddToCartParams) => Promise;
  doAddToCartAndRefresh: (
    params: AddToCartParams,
    convenienceChargesEnabled?: boolean
  ) => Promise;
  handleAddToCartTracking: (
    cartData: CartData,
    productData?: any,
    journeyData?: PageJourney
  ) => void;
  onSuccessAddToBag?: VoidFunction;
  pageType?: string;
  lang?: string;
  clickedFrom?: string;
  ctaText?: string;
  onErrorAddToBag: () => void;
  isVariantSelected: boolean;
  isVariantsAvailable: boolean;
  hideBagIcon?: boolean;
  isDefaultSelected?: boolean;
  isOnlyAddToCart?: boolean;
  newCtaText?: string;
  isGoToBag?: boolean;
  setNewCtaText: (prop: string) => void;
  setIsGoToBag: (prop: boolean) => void;
  journeyData: PageJourney;
  openedFrom?: string;
  handlePostAddToCart?: (increaseCount: number) => void;
  isConvSearch?: boolean;
  appTrackingKeys?: { [key: string]: any };
  readUrlParamsForTracking?: boolean;
}

function AddToCart({
  isPro,
  product,
  doAddToCart,
  doAddToCartAndRefresh,
  handleAddToCartTracking,
  onSuccessAddToBag,
  pageType,
  lang,
  clickedFrom = '',
  ctaText,
  onErrorAddToBag = () => {},
  isVariantSelected,
  isVariantsAvailable,
  hideBagIcon = false,
  isDefaultSelected = true,
  isOnlyAddToCart = false,
  newCtaText,
  setNewCtaText,
  isGoToBag,
  setIsGoToBag,
  journeyData,
  openedFrom = '',
  handlePostAddToCart,
  isConvSearch,
  appTrackingKeys,
  readUrlParamsForTracking = false,
}: AddToCartProps) {
  const ITEM_ADDED_TO_CART = useStringFromId(
    ADDED_TO_CART,
    PRODUCT_ADDED_TO_CART
  );
  const [isLoading, setIsLoading] = useState(false);
  const [addToBagMessage, setAddToBagMessage] = useState('');
  const [addToBagError, setAddToBagError] = useState(false);
  const [dealDeltaText, setDealDeltaText] = useState('');
  const { showToast: showDealDeltaToast, dismissToast: dismissDealDeltaToast } =
    useDealDeltaToast(dealDeltaText);

  const isExperimentEnabled = useSelector(
    (state: State) => state?.langConfig?.isExperimentEnabled
  );
  const dpPdpNativeViewEnabled = useSelector(
    (state: State) => state.remoteConfig?.switches?.dpPdpNativeViewEnabled
  );
  const device = useSelector((state: State) => state.appReducer?.client.device);
  const isPdpCtaExpEnabled = useSelector(
    (state: State) => state?.remoteConfig?.switches?.isPdpCtaEnabled
  );
  const isHlpCtaExpEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.isHlpCtaEnabled
  );
  const addItemRefreshApiEnabled = useSelector(
    (state: State) => state?.remoteConfig?.switches?.addItemRefreshApiEnabled
  );
  const cartApi = addItemRefreshApiEnabled
    ? doAddToCartAndRefresh
    : doAddToCart;
  const isActionCTARevamped = useSelector(
    (state: State) => state.remoteConfig.switches.isPdpActionV2Enabled
  );
  const isCtaDisableExp = useSelector((state: State) =>
    getEligibleForVariant(state, PRIVE_CTA_DISABLE, 'A')
  );
  const isConvenienceFeeEnabled = useIsConvenienceFeeEnabled();

  const { disablePriveProduct, productType, earlyAccessData } = product || {};
  const { disablePriveProduct: disableCTA } = earlyAccessData || {};
  const isPriveProductDisable = disablePriveProduct || disableCTA;
  const updateToGTB =
    (isPdpCtaExpEnabled && pageType === PageTypes.PRODUCT) ||
    (isHlpCtaExpEnabled && pageType === PageTypes.HLP);

  const disableAction =
    isCtaDisableExp &&
    isActionDisabled({
      pageTypeOrOpenedFrom: openedFrom,
      disablePriveProduct: isPriveProductDisable,
      productType: productType,
    });

  const theme: Theme = useTheme();
  const { childId, showOffer = false } = product;
  const handleSnackBarClose = () => {
    setAddToBagMessage('');
  };
  const {
    offer: offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    newButtonText,
    name,
  } = product;

  const handleAddToCart = async () => {
    if (disableAction) {
      return;
    }
    if (isDeviceNotWeb(device) && dpPdpNativeViewEnabled && !isConvSearch) {
      const appParams = {
        device,
        callbackFunctionName: webViewCallbacks.addProduct,
        callbackParams: {
          id: childId,
          name: name,
          param: { cartAddLoc: `App:${pageType}` },
        },
      };
      webViewCallbackhandler(appParams);
      return;
    }
    const pageSource = getViewBagPageSource();
    if (isGoToBag) {
      window.location.href = `${pages.CHECKOUT_PAGE}?rf=productDetailPage_GotoBag:ViewBag&source=${pageSource}`;
      return;
    }
    if (!isDefaultSelected && isVariantsAvailable && !isVariantSelected) {
      onErrorAddToBag();
      pushEvent('addToBag-withoutSize');
    } else {
      if (!isLoading) {
        let { siteNavigation = '', siteSubNavigation = '' } = journeyData || {};

        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get(REDIRECT_FROM) === COMBO_QUERY_PARAM) {
          (siteNavigation = PDP_COMBO_WIDGET),
            (siteSubNavigation = `${PDP_COMBO_WIDGET}:${product?.name}`),
            pushData({
              pageName: PDP_COMBO_WIDGET,
              siteNavigation,
              siteSubNavigation,
            });
        }
        let updatedSiteNav = siteNavigation;
        let updatedSiteSubNav = siteSubNavigation;
        if (readUrlParamsForTracking) {
          ({ updatedSiteNav, updatedSiteSubNav } = getUpdatedTrackingKeys({
            siteNavigation,
            siteSubNavigation,
            queryParams,
          }));
        }
        const trackingParamsUpdated = getNavigationTrackingParams(
          updatedSiteNav,
          updatedSiteSubNav
        );
        const params: AddToCartParams = {
          productId: childId,
          isPro,
          trackingParam: {
            ...trackingParamsUpdated,
          },
        };
        if (isConvSearch && params.trackingParam) {
          params.trackingParam.site_navigation =
            appTrackingKeys?.site_navigation || '';
          params.trackingParam.site_sub_navigation =
            appTrackingKeys?.site_sub_navigation || '';
        }
        setIsLoading(true);
        try {
          const cartData: CartData = await cartApi(
            params,
            isConvenienceFeeEnabled
          );
          if (handlePostAddToCart) handlePostAddToCart(1);
          if (cartData.dealDeltaText) {
            setDealDeltaText(cartData.dealDeltaText);
          }
          handleAddToCartTracking(cartData, product, journeyData);
          if (
            updateToGTB &&
            isValidFunc(setNewCtaText) &&
            isValidFunc(setIsGoToBag)
          ) {
            setNewCtaText(GO_TO_BAG);
            setIsGoToBag(true);
          }
          if (isOnlyAddToCart) {
            if (onSuccessAddToBag) onSuccessAddToBag();
          } else if (ctaText) {
            window.location.href = pages.CHECKOUT_PAGE;
          } else {
            setAddToBagError(false);
            setAddToBagMessage(ITEM_ADDED_TO_CART);
            const cabSectionNode =
              document.getElementById(CAB_SECTION_ID) ||
              document.getElementsByClassName(CAB_SECTION_ID)[0];
            if (cabSectionNode) {
              cabSectionNode.scrollIntoView({ behavior: SCROLL_BEHAVIOR });
            }
          }
          if (lang && isExperimentEnabled) {
            const trackingOptions = {
              eventName: ADD_TO_CART,
              isExperimentEnabled,
              product,
              clickedFrom,
              lang,
              action: ADD_TO_CART,
              offerMessage,
              offerId,
              showOffer,
            };
            productCardAction(trackingOptions);
          }
        } catch (e) {
          if (e.errorInApi) {
            setAddToBagMessage(e.message);
          } else {
            setAddToBagMessage(standardErrorMessages.SOMETHING_WRONG);
          }
          setAddToBagError(true);
        }
        setIsLoading(false);
      }
    }
    return;
  };

  const getBagIcon = (showAltIcon: boolean) => {
    if (!showAltIcon) {
      return <ShoppingBagIcon />;
    }

    return <ShoppingBagIconAlt />;
  };

  const shouldShowCTAText = !isLoading && (!newButtonText || !!ctaText);
  const showNewAddToBagText = !isLoading && !shouldShowCTAText && !isGoToBag;
  const showGoToBagText = !isLoading && isGoToBag;
  const showSnackbar =
    (!isPdpCtaExpEnabled && pageType === PageTypes.PRODUCT) ||
    (!isHlpCtaExpEnabled && pageType === PageTypes.HLP);
  return (
    <>
      <Button
        type="button"
        isOnlyAddToCart={isOnlyAddToCart}
        background={theme.brandPrimary}
        onClick={handleAddToCart}
        isLoading={isLoading}
        className="custom-cart"
        hideBag={hideBagIcon}
        isDisabled={disableAction}
      >
        {!hideBagIcon && getBagIcon(isActionCTARevamped)}
        <span className="btn-text">
          {isLoading && <FormattedMessage id={ADDING} />}
          {shouldShowCTAText && <FormattedMessage id={ctaText || ADD_TO_BAG} />}
          {showNewAddToBagText &&
            (isOnlyAddToCart ? (
              <FormattedMessage id={MOVE_TO_BAG} />
            ) : (
              newButtonText
            ))}
          {showGoToBagText && newCtaText}
        </span>
      </Button>
      {addToBagError
        ? addToBagMessage && (
            // eslint-disable-next-line react/jsx-indent
            <Toast
              variant={VARIANT.single}
              hideTimer
              message={addToBagMessage}
              timer={2}
              onDismiss={handleSnackBarClose}
            />
          )
        : (showSnackbar || !newCtaText) &&
          addToBagMessage && (
            <AddToCartSnackbar
              autoClose
              closeCallback={handleSnackBarClose}
              content={addToBagMessage}
              errorType={addToBagError}
              snackBarId={SNACKBAR_ID}
              pageType={pageType}
              device={device}
            />
          )}
      {showDealDeltaToast && dealDeltaText && (
        <Toast
          variant={VARIANT.single}
          hideTimer
          message={dealDeltaText}
          timer={3}
          onDismiss={() => {
            setDealDeltaText('');
            dismissDealDeltaToast();
          }}
        />
      )}
    </>
  );
}

export default AddToCart;
